import type { Manifest } from '@clawharbor/manifest';
import { renderDisclaimer, renderFreshnessChip, stringify } from '@clawharbor/render-core';
import type { Freshness } from '@clawharbor/render-core';
import { html } from '../html.js';
import type { VNode } from '../html.js';
import type { StagedSelection, ToolLogEntry, WebMcpStatus } from '../store.js';
import { mayPaintLogo, wordmarkText } from '../theme.js';

/**
 * Web-surface chrome.
 *
 * The disclaimer bar and freshness chip come straight from render-core so they
 * are literally the same components the MCP Apps widget shows. What lives here
 * is only what has no widget equivalent: a masthead, the agent debug strip, the
 * staged-selection panel, and a provenance footer.
 */

/**
 * Third-party notice, pinned above the fold.
 *
 * `checkReferentialIntegrity` already refuses to publish a third-party manifest
 * without `policy.disclaimer`, so this bar disappears on its own for a
 * self-owned tenant. Compliance as a code path, not a promise in a README.
 */
export function DisclaimerBar(manifest: Manifest): VNode | null {
  const bar = renderDisclaimer(manifest.policy, {
    origin: manifest.source.canonicalOrigin,
    // The footer already carries a takedown link; two is noise.
    hideTakedown: true,
  });
  if (!bar) return null;
  return html`<div class="chw-disclaimer-slot ch-root">${bar}</div>`;
}

export function Masthead(manifest: Manifest, freshness: Freshness | undefined, now: Date): VNode {
  const tagline =
    manifest.copy[manifest.identity.locale]?.['tagline'] ??
    manifest.copy['en']?.['tagline'] ??
    `Unofficial agent-facing view · data read live from ${new URL(manifest.source.canonicalOrigin).host}`;

  return html`
    <header class="chw-masthead">
      <div>
        <!--
          The logo file is painted ONLY when the manifest says we are allowed to:
          usage 'licensed' AND policy.assets.rehostImages. For a third-party
          tenant both are false, so the name is set in the tenant's own display
          face instead — reproducing a logo is a copyright act, and this branch
          is where that rule is enforced rather than merely documented.
        -->
        ${mayPaintLogo(manifest) && manifest.identity.logo
          ? html`<img
              class="chw-logo"
              src=${manifest.identity.logo.url}
              alt=${manifest.identity.displayName}
              height="40"
            />`
          : html`<h1 class="chw-wordmark">${wordmarkText(manifest)}</h1>`}
        <p class="chw-tagline">${tagline}</p>
      </div>
      <div class="chw-masthead-meta">
        ${renderFreshnessChip(freshness, now)}
        <a class="ch-chip" href=${manifest.source.canonicalOrigin} target="_blank" rel="noopener noreferrer">
          ${new URL(manifest.source.canonicalOrigin).host} ↗
        </a>
      </div>
    </header>
  `;
}

/**
 * The proof strip.
 *
 * WebMCP hosts show nothing when a tool runs — no spinner, no log, nothing. So
 * the page has to make invocation observable itself, or a live demo becomes a
 * claim instead of a demonstration. Renders exactly:
 *
 *   Agent tools: 4 registered · via document.modelContext · last call
 *   find_showtimes 220ms → 31 rows [agent]
 */
export function DebugStrip(status: WebMcpStatus, log: ToolLogEntry[]): VNode {
  const last = log[0];
  const on = status.available && status.registered > 0;

  const head = !status.available
    ? 'Agent tools: unavailable in this browser — the page works normally'
    : status.registered === 0
      ? 'Agent tools: none registered for this tenant'
      : `Agent tools: ${status.registered} registered`;

  return html`
    <div class="chw-debug" role="status" aria-live="polite">
      <span class=${`chw-led${on ? ' on' : ''}`} aria-hidden="true"></span>
      <span>${head}</span>
      ${status.available
        ? html`<span class="chw-sep">·</span
            ><span title="document.modelContext is current; navigator.modelContext is the deprecated alias"
              >via ${status.source}.modelContext</span
            >`
        : null}
      ${status.toolNames.length
        ? html`<span class="chw-sep">·</span><span>${status.toolNames.join(', ')}</span>`
        : null}
      <span class="chw-sep">·</span>
      ${last
        ? html`<span class=${last.ok ? '' : 'chw-bad'}>
            last call ${last.name} ${last.durationMs}ms${last.ok
              ? last.count == null
                ? ''
                : ` → ${last.count} rows`
              : ' (failed)'} [${last.origin}]
          </span>`
        : html`<span>no calls yet</span>`}
      ${status.error ? html`<span class="chw-sep">·</span><span class="chw-bad">${status.error}</span>` : null}
    </div>
  `;
}

/**
 * A staged selection is a PROPOSAL, never a purchase.
 *
 * WebMCP has no `requestUserInteraction` — it was deleted from the spec — so the
 * human-in-the-loop pattern has to be structural instead of an API call: the
 * agent stages, the page shows it, and only a real human press on a real link
 * completes anything, on the operator's own checkout.
 */
export function StagedPanel(staged: StagedSelection | undefined, onClear: () => void): VNode | null {
  if (!staged) return null;

  const detail = Object.entries(staged.fields)
    .map(([k, v]) => [k, stringify(v)] as const)
    .filter(([, v]) => v.length > 0)
    .slice(0, 6)
    .map(([k, v]) => `${k}: ${v}`);

  return html`
    <section class="chw-staged ch-root" aria-label="Staged selection">
      <div class="chw-staged-label">Staged — nothing has been booked</div>
      <strong>${staged.label}</strong>
      ${detail.length ? html`<div class="chw-hint">${detail.join(' · ')}</div>` : null}
      <div class="chw-actions">
        ${staged.deepLink
          ? html`<a class="ch-btn" href=${staged.deepLink} target="_blank" rel="noopener noreferrer"
              >Buy on the official site ↗</a
            >`
          : null}
        <button class="ch-btn ch-btn--ghost" type="button" onClick=${onClear}>Discard</button>
      </div>
      <p class="chw-hint" style="margin:8px 0 0">
        No seat is reserved. Payment happens on the operator's own site, in your browser.
      </p>
    </section>
  `;
}

export function Footer(manifest: Manifest): VNode {
  const origin = new URL(manifest.source.canonicalOrigin).host;
  const policy = manifest.policy;

  return html`
    <footer class="chw-footer">
      <div>
        Data read live from
        <a href=${manifest.source.canonicalOrigin} target="_blank" rel="noopener noreferrer">${origin}</a>${policy.data
          .retainHistory
          ? ''
          : '; no history is retained'}, horizon ${policy.data.maxHorizonDays} days, max
        ${policy.data.maxRps} req/s.
      </div>
      <div>
        Manifest ${manifest.manifestVersion} · generated ${manifest.generatedAt.slice(0, 10)} by
        ${manifest.generator.name} (${manifest.generator.mode})${manifest.contentHash
          ? ` · ${manifest.contentHash.slice(0, 12)}`
          : ''}
      </div>
      <div>
        <a href=${policy.takedownUrl} target="_blank" rel="noopener noreferrer">Report an issue / takedown</a>${manifest
          .identity.logo
          ? ' · Brand assets are referenced, not reproduced.'
          : ''}
      </div>
    </footer>
  `;
}
